import {
  Box,
  Breadcrumbs,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Company } from "../models/Companies/Company";
import { User } from "../models/Users/User";
import { Subscription } from "../models/Subscription/Subscription";
import agent from "../../api/agent";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import { jwtDecode } from "jwt-decode";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LicenseDetails from "./LicenseDetails";
import { UserCreate } from "../models/Users/UserCreate";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockResetIcon from "@mui/icons-material/LockReset";
import CloseIcon from "@mui/icons-material/Close";
import CreateUserForm from "../forms/Users/CreateUserForm";
import EditUserForm from "../forms/Users/EditUserForm";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";
import ResetPassword from "../models/Users/ResetPassword";
import PasswordResetForm from "../forms/Users/PasswordResetForm";
import LicenseByUser from "./LicenseByUser";

export default function CompanyDetails() {
  const { companyId } = useParams();
  //   change company Id to number and store for future usage
  const [companyIdInt] = useState<number>(Number(companyId));
  const [loading, setLoading] = useState<boolean>(true);
  const [companyDetail, setCompanyDetail] = useState<Company>();
  const [userList, setUserList] = useState<User[]>([]);
  const [subscriptionList, setSubscriptionList] = useState<Subscription[]>([]);
  const [activeTabs, setActiveTabs] = useState<string>("Users");

  // For user part
  const [createFormOpenState, setCreateFormOpenState] =
    useState<boolean>(false);
  const [editFormOpenState, setEditFormOpenState] = useState<boolean>(false);
  const [deleteDialogOpenState, setDeleteDialogOpenState] =
    useState<boolean>(false);

  const [selectedUser, setSelectedUser] = useState<UserCreate | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);

  // For license detail dialog
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<number>(0);
  const [licenseDetailDialogOpenState, setLicenseDetailDialogOpenState] =
    useState<boolean>(false);
  const [maxPax, setMaxPax] = useState<number>(0);
  const [softwarePrefixName, setSoftwarePrefixName] = useState<
    string | undefined
  >(undefined);

  // Pagination (User and Subscription)
  const [pageForUser, setPageForUser] = useState(0);
  const [rowsPerPageForUser, setRowsPerPageForUser] = useState(50);

  const [pageForSubs, setPageForSubs] = useState(0);
  const [rowsPerPageForSubs, setRowsPerPageForSubs] = useState(50);

  //For User password reset
  const [pwResetFormOpenState, setPwResetFormOpenState] =
    useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<string>("");

  // Authentication
  const [token, setToken] = useState<string>("");

  // Authorization
  const [userRole, setUserRole] = useState<string>("");

  // Show License Details by User
  const [licenseDetailsFormOpenState, setLicenseDetailsFormOpenState] =
    useState<boolean>(false);
  const [selectedUserIdForLicense, setSelectedUserIdForLicense] = useState<
    number | undefined
  >(undefined);

  //Function for change the content when click on the tabs
  function handleTabChange(event: React.SyntheticEvent, selectedValue: string) {
    setActiveTabs(selectedValue);
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          const decodedToken: any = jwtDecode(tokenFLocal);
          const userId = decodedToken.sub;
          // to get the user role for user logged in
          const userDetailsResponse = await agent.User.getUserDetail(
            userId,
            tokenFLocal,
          );
          setUserRole(userDetailsResponse.role);
          setToken(tokenFLocal);

          // To get the company details
          const companyDetailsResponse = await agent.Company.getCompanyDetail(
            companyIdInt,
            tokenFLocal,
          );
          setCompanyDetail(companyDetailsResponse);

          // To get the user List
          const userResponse = await agent.User.getUserByCompanyId(
            companyIdInt,
            tokenFLocal,
          );
          setUserList(userResponse);

          // To get the Subscription list
          const subscriptionResponse =
            await agent.Subscription.getSubscriptionByCompanyId(
              companyIdInt,
              tokenFLocal,
            );
          setSubscriptionList(subscriptionResponse);

          // close the loader after finish fetching data
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Function to open license details dialog (eye Icon)
  function handleLicenseDetailsDialogOpen(
    subscriptionId: number,
    paxNumber: number | undefined,
    softwarePrefixName: string | undefined,
  ) {
    setSoftwarePrefixName(softwarePrefixName);
    setSelectedSubscriptionId(subscriptionId);
    if (paxNumber !== undefined) {
      setMaxPax(paxNumber);
    }
    setLicenseDetailDialogOpenState(true);
  }

  // Function to Close license details Dialog
  function handleLicenseDetailsDialogClose() {
    setSoftwarePrefixName(undefined);
    setSelectedSubscriptionId(0);
    setMaxPax(0);
    setLicenseDetailDialogOpenState(false);
  }

  // Function for pagination
  const handleChangePageForSubs = (event: unknown, newPage: number) => {
    setPageForSubs(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPageForSubs = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPageForSubs(parseInt(event.target.value, 10));
    setPageForSubs(0);
  };

  // ****************Function for User Tab*****************
  // Function for open edit form
  function handleCreateFormOpen() {
    setCreateFormOpenState(true);
  }

  function handleEditFormOpen(userId: number) {
    const getUser = userList.find((x) => x.userId === userId);
    if (getUser) {
      // Map User to UserCreate
      const userCreate: UserCreate = {
        email: getUser.email,
        userName: getUser.userName,
        phoneNumber: getUser.phoneNumber,
        role: getUser.role,
        companyId: getUser.companyId,
        // Add other properties as needed
      };
      setSelectedUser(userCreate);
      setSelectedUserId(userId);
      setEditFormOpenState(true);
    }
  }

  function handleCreateOrEditFormClose() {
    setCreateFormOpenState(false);
    setEditFormOpenState(false);
    setSelectedUser(null);
    setSelectedUserId(0);
  }

  function handleDeleteOnClick(userId: number) {
    setSelectedUserId(userId);
    setDeleteDialogOpenState(true);
  }

  function handleDeleteDialogClose() {
    setSelectedUserId(0);
    setDeleteDialogOpenState(false);
  }

  // Function to create new User
  async function handleCreateSubmit(formData: UserCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.create(formData, token).then(async () => {
        const updatedUser = await agent.User.getUserByCompanyId(
          companyIdInt,
          token,
        );
        setUserList(updatedUser);
        handleCreateOrEditFormClose();
        showToast("User Created Successfully", "success");
      });
    } catch (error) {
      console.error("Error updating user:", error);
      showToast("Error Creating user", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to edit User details
  async function handleEditSubmit(formData: UserCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.update(selectedUserId, formData, token).then(
        async () => {
          const updatedUsers = await agent.User.getUserByCompanyId(
            companyIdInt,
            token,
          );
          setUserList(updatedUsers);
          handleCreateOrEditFormClose();
          showToast("User Edited Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error updating user:", error);
      showToast("Error Updating user", "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedUserId === 0) return;
    setLoading(true);
    try {
      await agent.User.delete(selectedUserId, token).then(async () => {
        const updatedUser = await agent.User.getUserByCompanyId(
          companyIdInt,
          token,
        );
        setUserList(updatedUser);
        handleDeleteDialogClose();
        showToast("User Deleted Successfully", "success");
      });
    } catch (error) {
      console.error("Error Deleting User:", error);
      showToast("Error Deleting user", "error");
    } finally {
      setLoading(false);
      handleDeleteDialogClose(); //Reset all the delete form open state
    }
  }

  // Function for user Password Reset
  function handlePWResetFormOpen(email: string) {
    setSelectedEmail(email);
    setPwResetFormOpenState(true);
  }

  function handlePWResetFormClose() {
    setSelectedEmail("");
    setPwResetFormOpenState(false);
  }

  async function handlePWReset(formData: ResetPassword) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.User.resetPassword(selectedEmail, formData, token).then(
        () => {
          handlePWResetFormClose();
          showToast("User Password Reset Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error reset user Password:", error);
      showToast("Error Reset User Password", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function for View License Details by User
  function handleLicenseDetailsFormOpen(userId: number) {
    setSelectedUserIdForLicense(userId);
    setLicenseDetailsFormOpenState(true);
  }

  function handleLicenseDetailsFormClose() {
    setSelectedUserIdForLicense(undefined);
    setLicenseDetailsFormOpenState(false);
  }

  // Function for pagination
  const handleChangePageForUser = (event: unknown, newPage: number) => {
    setPageForUser(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPageForUser = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPageForUser(parseInt(event.target.value, 10));
    setPageForUser(0);
  };

  // Reuseable component for user pagination
  const UserPaginationComponent = () => (
    <TablePagination
      rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
      component="div"
      count={userList.length} // Total number of rows
      rowsPerPage={rowsPerPageForUser}
      page={pageForUser}
      onPageChange={handleChangePageForUser}
      onRowsPerPageChange={handleChangeRowsPerPageForUser}
      sx={{
        ".MuiTablePagination-toolbar": {
          paddingLeft: 2, // Adjust padding as needed
        },
      }}
    />
  );

  // Reuseable component for subscription pagination
  const SubscriptionPaginationComponent = () => (
    <TablePagination
      rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
      component="div"
      count={subscriptionList.length} // Total number of rows
      rowsPerPage={rowsPerPageForSubs}
      page={pageForSubs}
      onPageChange={handleChangePageForSubs}
      onRowsPerPageChange={handleChangeRowsPerPageForSubs}
      sx={{
        ".MuiTablePagination-toolbar": {
          paddingLeft: 2, // Adjust padding as needed
        },
      }}
    />
  );

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;

  return (
    <Box sx={{ p: 2 }}>
      {/* Only show to Super Admin level */}
      {userRole === "Super Admin" && (
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" href="/company">
            Company List
          </Link>
          <Typography sx={{ color: "text.primary" }}>Details</Typography>
        </Breadcrumbs>
      )}

      <Typography variant="h5" sx={{ mb: 3 }}>
        Company Details ({companyDetail?.companyName},{" "}
        {companyDetail?.companyCode})
      </Typography>

      {/* Tabs Section */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTabs}
          onChange={handleTabChange}
          // variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value="Users"
            //   icon={<People />}
            iconPosition="start"
            label="Users"
            sx={{ textTransform: "none" }}
          />
          <Tab
            value="Subscriptions"
            //   icon={<Subscriptions />}
            iconPosition="start"
            label="Subscriptions"
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {activeTabs === "Users" && (
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            >
              <Typography variant="h6">Company Users</Typography>
              {/* <Tooltip title="Create New" followCursor>
                <Fab
                  color="primary"
                  onClick={() => handleCreateFormOpen()}
                  size="small"
                >
                  <AddIcon />
                </Fab>
              </Tooltip> */}
              <UserPaginationComponent />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>UserName</TableCell>
                    <TableCell>Phone Number</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      Number of Licenses Assigned
                    </TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {userList
                    .slice(
                      pageForUser * rowsPerPageForUser,
                      pageForUser * rowsPerPageForUser + rowsPerPageForUser,
                    )
                    .map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.userName}</TableCell>
                        <TableCell>{user.phoneNumber}</TableCell>
                        <TableCell>{user.role}</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          {user.licenseCount}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0 16px", // Reduce padding (default is 16px)
                            width: "2%", // Minimize width to content
                            whiteSpace: "nowrap", // Prevent wrapping
                          }}
                        >
                          <Stack direction="row" spacing={1}>
                            {/* Button for View License Details */}
                            <Tooltip
                              title={
                                user.licenseCount === 0
                                  ? "This user has no licenses"
                                  : "View/Edit Licenses"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleLicenseDetailsFormOpen(user.userId)
                                  }
                                  disabled={user.licenseCount === 0}
                                  sx={{
                                    opacity: user.licenseCount === 0 ? 0.5 : 1,
                                  }}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {/* Button for password reset */}
                            <Tooltip title="User Password Reset">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handlePWResetFormOpen(user.email)
                                }
                              >
                                <LockResetIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit User">
                              <IconButton
                                color="success"
                                size="small"
                                onClick={() => handleEditFormOpen(user.userId)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete User">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteOnClick(user.userId)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              {/* Pagination Component (User Table) */}
              <UserPaginationComponent />
            </TableContainer>

            {/* --------------------- User MODEL------------------------------- */}
            {/* Model for user create form */}
            <Dialog
              open={createFormOpenState}
              onClose={handleCreateOrEditFormClose}
              maxWidth="lg"
              fullWidth
              slotProps={{
                backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
                paper: {
                  sx: {
                    width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
                    maxWidth: "960px", // Matches 'md' breakpoint
                    maxHeight: "80vh", // Limit height
                    overflowY: "auto", // Scroll if needed
                    borderRadius: 2,
                  },
                },
              }}
            >
              <DialogTitle>
                <Typography variant="h6" component="h2">
                  Create New User
                </Typography>
              </DialogTitle>
              <DialogContent>
                <CreateUserForm
                  selectedCompanyId={undefined}
                  closeForm={handleCreateOrEditFormClose}
                  create={handleCreateSubmit}
                  fromUserDashboard={false}
                />
              </DialogContent>
            </Dialog>

            {/* Model for user edit form */}
            <Dialog
              open={editFormOpenState}
              onClose={handleCreateOrEditFormClose}
              maxWidth="lg"
              fullWidth
              slotProps={{
                backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
                paper: {
                  sx: {
                    width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
                    maxWidth: "960px", // Matches 'md' breakpoint
                    maxHeight: "80vh", // Limit height
                    overflowY: "auto", // Scroll if needed
                    borderRadius: 2,
                  },
                },
              }}
            >
              <DialogTitle>
                <Typography variant="h6" component="h2">
                  Edit User Details
                </Typography>
              </DialogTitle>
              <DialogContent>
                {selectedUser && (
                  <EditUserForm
                    selectedUser={selectedUser}
                    closeForm={handleCreateOrEditFormClose}
                    edit={handleEditSubmit}
                    fromUserDashboard={false}
                  />
                )}
              </DialogContent>
            </Dialog>

            {/* Model for user Password Reset form */}
            <Dialog
              open={pwResetFormOpenState}
              onClose={handlePWResetFormClose}
              maxWidth="lg"
              fullWidth
              slotProps={{
                backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
                paper: {
                  sx: {
                    width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
                    maxWidth: "960px", // Matches 'md' breakpoint
                    maxHeight: "80vh", // Limit height
                    overflowY: "auto", // Scroll if needed
                    borderRadius: 2,
                  },
                },
              }}
            >
              <DialogTitle>
                <Typography variant="h6" component="h2">
                  User Password Reset
                </Typography>
              </DialogTitle>
              <DialogContent>
                <PasswordResetForm
                  email={selectedEmail}
                  closeForm={handlePWResetFormClose}
                  passwordReset={handlePWReset}
                />
              </DialogContent>
            </Dialog>

            {/* Model for license details form */}
            <Dialog
              open={licenseDetailsFormOpenState}
              onClose={handleLicenseDetailsFormClose}
              maxWidth="lg"
              fullWidth
              slotProps={{
                backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
                paper: {
                  sx: {
                    width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
                    maxWidth: "960px", // Matches 'md' breakpoint
                    maxHeight: "80vh", // Limit height
                    overflowY: "auto", // Scroll if needed
                    borderRadius: 2,
                  },
                },
              }}
            >
              <DialogTitle
                sx={{
                  ml: 2,
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" component="h2">
                  License Details
                </Typography>

                <IconButton
                  aria-label="close"
                  onClick={handleLicenseDetailsFormClose}
                  sx={{
                    color: (theme) => theme.palette.grey[500],
                    "&:hover": {
                      color: (theme) => theme.palette.grey[700],
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                {/* {selectedUser && ( */}
                <LicenseByUser
                  userIdForChecking={selectedUserIdForLicense}
                  closeForm={handleLicenseDetailsFormClose}
                />
                {/* )} */}
              </DialogContent>
            </Dialog>

            <DeleteConfirmationBox
              open={deleteDialogOpenState}
              onClose={handleDeleteDialogClose}
              onConfirm={handleConfirmDelete}
            />
          </Paper>
        )}

        {activeTabs === "Subscriptions" && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6">Company Subscription</Typography>
              <SubscriptionPaginationComponent />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Software Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>Valid Date</TableCell>
                    <TableCell>Pax</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {subscriptionList
                    .slice(
                      pageForSubs * rowsPerPageForSubs,
                      pageForSubs * rowsPerPageForSubs + rowsPerPageForSubs,
                    )
                    .map((subscription) => (
                      <TableRow key={subscription.subscriptionId}>
                        <TableCell>{subscription.companyName}</TableCell>
                        <TableCell>{subscription.softwareName}</TableCell>
                        <TableCell>
                          {" "}
                          {subscription.status === "Active" ? (
                            <Chip
                              label="Active"
                              color="success"
                              variant="outlined"
                            />
                          ) : subscription.status === "Disable" ? (
                            <Chip label="Disable" variant="outlined" />
                          ) : (
                            <Chip
                              label="End Service"
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>{subscription.startDate}</TableCell>
                        <TableCell>{subscription.validDate}</TableCell>
                        <TableCell>{subscription.paxNo}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {/* Button for View License (In Dialog opening) */}
                            <Tooltip title="View Licenses">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleLicenseDetailsDialogOpen(
                                    subscription.subscriptionId,
                                    subscription.paxNo,
                                    subscription.prefixName,
                                  )
                                }
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              {/* Pagination Component (Subscription Table) */}
              <SubscriptionPaginationComponent />
            </TableContainer>

            {/* --------------------- Subsciption MODEL------------------------------- */}
            {/* Dialog to show License Details */}
            <Dialog
              open={licenseDetailDialogOpenState}
              onClose={handleLicenseDetailsDialogClose}
              maxWidth="lg"
              fullWidth
              slotProps={{
                backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
                paper: {
                  sx: {
                    width: { xs: "90%", sm: "80%", md: "90%" }, // Responsive width
                    maxWidth: "9600px", // Matches 'md' breakpoint
                    maxHeight: "80vh", // Limit height
                    overflowY: "auto", // Scroll if needed
                    borderRadius: 2,
                  },
                },
              }}
            >
              <DialogTitle
                sx={{
                  ml: 2,
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" component="h2">
                  License Details
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={handleLicenseDetailsDialogClose}
                  sx={{
                    color: (theme) => theme.palette.grey[500],
                    "&:hover": {
                      color: (theme) => theme.palette.grey[700],
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <LicenseDetails
                  softwarePrefixName={softwarePrefixName}
                  closeDialog={handleLicenseDetailsDialogClose}
                  selectedSubscriptionId={selectedSubscriptionId}
                  maxPaxNo={maxPax}
                  userRole={userRole}
                />
              </DialogContent>
            </Dialog>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
